import { useEffect, useState } from "react"
import { useSearchParams, Link } from "react-router"
import { useAuthStore } from "@/shared/hooks/use-auth-store"
import { getBrowserSupabaseClient } from "@/lib/supabase/client"
import styles from "./auth-callback-page.module.css"

type VerifyStatus = "loading" | "success" | "error" | "expired"

export default function AuthCallbackPage() {
  const [searchParams] = useSearchParams()
  const { isAuthenticated, isLoading: authLoading } = useAuthStore()
  const [status, setStatus] = useState<VerifyStatus>("loading")
  const [errorMessage, setErrorMessage] = useState("")
  const [email, setEmail] = useState("")

  useEffect(() => {
    async function handleCallback() {
      const supabase = getBrowserSupabaseClient()
      if (!supabase) {
        setStatus("error")
        setErrorMessage("系统未正确配置，请稍后重试")
        return
      }

      // 获取 URL 参数
      const token = searchParams.get("token")
      const type = searchParams.get("type")
      const errorParam = searchParams.get("error")
      const errorCode = searchParams.get("error_code")

      // 处理错误情况
      if (errorParam || errorCode) {
        setStatus("error")
        if (errorCode === "unauthorized") {
          setErrorMessage("链接已过期，请重新获取验证邮件")
        } else if (errorCode === "invalid_token") {
          setErrorMessage("验证链接无效，请检查邮件中的链接是否正确")
        } else {
          setErrorMessage(errorParam || "验证失败，请稍后重试")
        }
        return
      }

      // 处理邮箱验证确认 (Supabase 邮箱确认回调)
      if (type === "email" && token) {
        const emailParam = searchParams.get("email")
        try {
          // 调用 Edge Function 验证邮箱 token
          const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
          const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY

          if (!supabaseUrl || !supabaseKey) {
            setStatus("error")
            setErrorMessage("系统配置不完整，请联系管理员")
            return
          }

          const response = await fetch(
            `${supabaseUrl}/functions/v1/verify-email`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${supabaseKey}`,
              },
              body: JSON.stringify({ token, email: emailParam }),
            }
          )

          const result = await response.json()

          if (response.ok && result.success) {
            setStatus("success")
            setEmail(result.email || emailParam || "")
            // 存储邮箱以便后续使用
            if (result.email) {
              localStorage.setItem("verified_email", result.email)
            }
          } else if (response.status === 410) {
            // Gone - 链接已过期
            setStatus("expired")
            setErrorMessage(result.message || "验证链接已过期，请重新注册")
          } else {
            setStatus("error")
            setErrorMessage(result.message || "邮箱验证失败，请重新获取验证邮件")
          }
        } catch {
          setStatus("error")
          setErrorMessage("验证过程出现异常，请稍后重试")
        }
        return
      }

      // 处理其他 OAuth 回调
      if (type === "oauth") {
        // OAuth 回调由 AuthInitializer 的 onAuthStateChange 处理
        // 这里只需要等待状态变化
        if (isAuthenticated) {
          setStatus("success")
        }
        return
      }

      // 未知类型，保持加载状态
    }

    handleCallback()
  }, [searchParams, isAuthenticated])

  // 从 localStorage 获取注册邮箱
  useEffect(() => {
    const registeredEmail = localStorage.getItem("pending_verification_email")
    if (registeredEmail) {
      setEmail(registeredEmail)
      localStorage.removeItem("pending_verification_email")
    }
  }, [])

  // 模拟等待认证状态
  useEffect(() => {
    if (authLoading) return
    if (status !== "loading") return

    // 如果 3 秒后仍未确定状态，显示错误
    const timeout = setTimeout(() => {
      if (status === "loading") {
        setStatus("error")
        setErrorMessage("验证超时，请稍后重试")
      }
    }, 3000)

    return () => clearTimeout(timeout)
  }, [authLoading, status])

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        {status === "loading" && (
          <div className={styles.statusBox}>
            <div className={styles.spinner}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83">
                  <animateTransform
                    attributeName="transform"
                    type="rotate"
                    from="0 12 12"
                    to="360 12 12"
                    dur="1s"
                    repeatCount="indefinite"
                  />
                </path>
              </svg>
            </div>
            <h2 className={styles.title}>验证中...</h2>
            <p className={styles.desc}>正在完成邮箱验证，请稍候</p>
          </div>
        )}

        {status === "success" && (
          <div className={styles.statusBox}>
            <div className={`${styles.iconBox} ${styles.successIcon}`}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h2 className={styles.title}>验证成功</h2>
            <p className={styles.desc}>
              {email ? `已向 ${email} 发送验证确认` : "邮箱验证已完成"}
            </p>
            <div className={styles.actions}>
              <Link to="/me" className={styles.primaryBtn}>
                前往个人中心
              </Link>
              <Link to="/discovery" className={styles.secondaryBtn}>
                探索更多内容
              </Link>
            </div>
          </div>
        )}

        {status === "error" && (
          <div className={styles.statusBox}>
            <div className={`${styles.iconBox} ${styles.errorIcon}`}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </div>
            <h2 className={styles.title}>验证失败</h2>
            <p className={styles.desc}>{errorMessage}</p>
            <div className={styles.actions}>
              <Link to="/register" className={styles.primaryBtn}>
                重新注册
              </Link>
              <button
                className={styles.secondaryBtn}
                onClick={() => window.history.back()}
              >
                返回上一页
              </button>
            </div>
          </div>
        )}

        {status === "expired" && (
          <div className={styles.statusBox}>
            <div className={`${styles.iconBox} ${styles.expiredIcon}`}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            </div>
            <h2 className={styles.title}>链接已过期</h2>
            <p className={styles.desc}>
              验证链接已过期，请重新注册或联系客服获取新的验证邮件
            </p>
            <div className={styles.actions}>
              <Link to="/register" className={styles.primaryBtn}>
                立即注册
              </Link>
              <Link to="/login" className={styles.secondaryBtn}>
                返回登录
              </Link>
            </div>
          </div>
        )}
      </div>

      {/* 背景装饰 */}
      <div className={styles.bgPattern} />
    </div>
  )
}